import { useState } from "react";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { authStore, useAuth } from "../../auth/store";
import { useCan } from "../../auth/roles";
import { ErrorState } from "../../components/States";
import { BusyButton, ConfirmDialog, CopyButton, DataTable, Dialog, Field, RowMenu, useToast } from "../../components/ui";
import { formatRelative } from "../../lib/format";
import { backendApi, backendKeys, type CaptureLevel, type ServerKey } from "./api";

/** A key the caller just made. Its secret is shown once and then only its prefix. */
export interface NewKey {
  key: ServerKey;
  secret: string;
}

const LEVELS: Array<{ id: CaptureLevel; label: string; hint: string }> = [
  { id: "everything", label: "Everything", hint: "Every request and every log line, including debug." },
  { id: "standard", label: "Standard", hint: "Every request, and info logs and above." },
  { id: "critical", label: "Problems only", hint: "Only requests that fail, run slow, or come from a replay, with their warnings and errors." },
  { id: "off", label: "Off", hint: "Keep nothing. Servers stay connected, and you can turn this back on here." },
];

export function levelLabel(level?: string): string {
  return LEVELS.find((l) => l.id === level)?.label ?? "Standard";
}

/** Backend keys for every app in the organization, with what each app keeps. */
export function OrgBackendKeys({ projects }: { projects: Array<{ id: string; name: string }> }) {
  const can = useCan();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const lists = useQueries({
    queries: projects.map((p) => ({ queryKey: backendKeys.keys(p.id), queryFn: () => backendApi.keys(p.id) })),
  });
  const settings = useQueries({
    queries: projects.map((p) => ({ queryKey: backendKeys.settings(p.id), queryFn: () => backendApi.settings(p.id) })),
  });
  const levelOf = new Map(projects.map((p, i) => [p.id, settings[i]?.data?.settings.captureLevel]));
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState<NewKey | null>(null);
  const [revoking, setRevoking] = useState<ServerKey | null>(null);

  const rows = projects.flatMap((p, i) => (lists[i]?.data?.keys ?? []).filter((k) => !k.revokedAt).map((k) => ({ ...k, app: p.name })));
  const failed = lists.find((q) => q.isError);
  const loading = projects.length > 0 && lists.some((q) => q.isPending);

  const revoke = useMutation({
    mutationFn: (k: ServerKey) => backendApi.revokeKey(k.projectId, k.id),
    onSuccess: (_d, k) => {
      setRevoking(null);
      void qc.invalidateQueries({ queryKey: backendKeys.keys(k.projectId) });
      toast.push("info", "Backend key revoked.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });

  return (
    <>
      <div className="be-keys-head">
        {can.admin && <button type="button" className="btn primary" onClick={() => setOpen(true)} disabled={projects.length === 0}>New backend key</button>}
      </div>
      {failed ? (
        <ErrorState error={failed.error} retry={() => void lists.forEach((q) => q.refetch())} />
      ) : (
        <div className="stack-table be-table">
          <DataTable<ServerKey & { app: string }>
            ariaLabel="Backend keys"
            rows={rows}
            rowKey={(k) => k.id}
            loading={loading}
            empty={projects.length === 0 ? "Add an app first. Each backend key belongs to one app." : "No backend keys yet."}
            columns={[
              { key: "name", header: "Name", label: "Name", render: (k) => (<><strong>{k.name || "Backend key"}</strong><div className="muted small mono">{k.prefix}…</div></>) },
              { key: "app", header: "App", label: "App", render: (k) => k.app },
              { key: "env", header: "Environment", label: "Environment", render: (k) => (k.environment ? <span className="badge neutral">{k.environment}</span> : <span className="muted">Any</span>) },
              { key: "keep", header: "What it keeps", label: "What it keeps", render: (k) => <span className="badge neutral">{levelLabel(levelOf.get(k.projectId))}</span> },
              { key: "used", header: "Last used", label: "Last used", render: (k) => (k.lastUsedAt ? formatRelative(k.lastUsedAt) : <span className="muted">Never</span>) },
              {
                key: "act",
                header: "",
                className: "row-actions",
                width: 44,
                align: "right",
                render: (k) => (
                  <RowMenu label={`Actions for ${k.name || k.prefix}`}>
                    <button type="button" role="menuitem" onClick={() => { authStore.setProject(k.projectId); void navigate({ to: "/backend/settings" }); }}>Settings</button>
                    {can.admin && <button type="button" role="menuitem" className="danger" onClick={() => setRevoking(k)}>Revoke key</button>}
                  </RowMenu>
                ),
              },
            ]}
          />
        </div>
      )}

      <CreateKeyDialog
        open={open}
        projects={projects}
        onClose={() => setOpen(false)}
        onCreated={(k) => {
          setOpen(false);
          setCreated(k);
          void qc.invalidateQueries({ queryKey: backendKeys.keys(k.key.projectId) });
        }}
      />
      <Dialog open={!!created} onClose={() => setCreated(null)} title="Backend key created" footer={<button type="button" className="btn primary" onClick={() => setCreated(null)}>Done</button>}>
        <p className="muted">Copy it now. You can't see the full key again after you close this.</p>
        <div className="mono-box" data-testid="server-key-secret">{created?.secret}</div>
        <div style={{ marginTop: 8 }}>
          <CopyButton value={created?.secret ?? ""} label="Copy key" />
        </div>
      </Dialog>
      <ConfirmDialog
        open={!!revoking}
        danger
        title="Revoke backend key"
        confirmLabel="Revoke key"
        busy={revoke.isPending}
        onClose={() => { if (!revoke.isPending) setRevoking(null); }}
        onConfirm={() => revoking && revoke.mutate(revoking)}
      >
        <p style={{ margin: 0 }}><strong>{revoking?.name || revoking?.prefix}</strong> stops working right away. Any server using it stops sending data.</p>
      </ConfirmDialog>
    </>
  );
}


function CreateKeyDialog({ open, onClose, onCreated, projects }: { open: boolean; onClose: () => void; onCreated: (k: NewKey) => void; projects?: Array<{ id: string; name: string }> }) {
  const auth = useAuth();
  const toast = useToast();
  const [projectId, setProjectId] = useState("");
  const [name, setName] = useState("");
  const [env, setEnv] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const chosen = projects?.length === 1 ? projects[0]!.id : projectId || (projects ? "" : auth.currentProjectId ?? "");
  const nameErr = name.trim().length > 80 ? "Use 80 characters or fewer." : errors.name;
  const envErr = env.trim().length > 40 ? "Use 40 characters or fewer." : errors.environment;
  const create = useMutation({
    mutationFn: () => backendApi.createKey(chosen, { name: name.trim(), environment: env.trim() }),
    onSuccess: (k) => {
      setName("");
      setEnv("");
      setErrors({});
      onCreated(k);
    },
    onError: (e) => {
      const err = e as Error & { field?: string; body?: { field?: string } };
      const field = err.field ?? err.body?.field;
      if (field) setErrors({ [field]: err.message });
      else toast.push("error", err.message);
    },
  });
  const ready = name.trim() !== "" && chosen !== "" && !nameErr && !envErr;
  return (
    <Dialog
      open={open}
      onClose={() => { if (!create.isPending) onClose(); }}
      title="New backend key"
      width={480}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={create.isPending}>Cancel</button>
          <BusyButton className="btn primary" busy={create.isPending} busyLabel="Creating…" disabled={!ready} onClick={() => create.mutate()}>Create key</BusyButton>
        </>
      }
    >
      {projects && projects.length > 1 && (
        <Field label="App">
          {(p) => (
            <select {...p} className="input" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">Choose an app</option>
              {projects.map((app) => <option key={app.id} value={app.id}>{app.name}</option>)}
            </select>
          )}
        </Field>
      )}
      <Field label="Name" error={nameErr} hint="Which service or team uses it.">
        {(p) => <input {...p} className="input" value={name} onChange={(e) => { setName(e.target.value); setErrors({}); }} placeholder="Checkout service" autoFocus />}
      </Field>
      <Field label="Environment" error={envErr} hint="Optional. Leave empty to accept data from any environment.">
        {(p) => <input {...p} className="input" value={env} onChange={(e) => { setEnv(e.target.value); setErrors({}); }} placeholder="production" />}
      </Field>
    </Dialog>
  );
}
